import React, { useEffect, useState } from 'react'
import styles from 'styles/components/ProfileHeaderSummary.module.sass'
import BasicProfile from 'interface/BasicProfile'
import ProfilePlanSummaryCard from 'components/cards/ProfilePlanSummaryCard';
import { useAuth } from 'hooks/AuthContext';
import ReusableIcon from 'components/common/ReusableIcon';
import { useNavigate } from 'react-router-dom';
import ProfilePictureWrapper from 'components/common/ProfilePictureWrapper';
import { useToast } from 'provider/ToastProvider';

/**
 * Microsoft Forms pilot feedback survey. Mirrors the same URL wired into
 * PilotEndedDialog and the workspace's FeedbackButton. A dedicated
 * in-workspace feedback form is planned but not yet built - this is the
 * interim destination.
 */
const PILOT_FEEDBACK_FORM_URL = 'https://forms.cloud.microsoft/r/dpXGJSsRvN'

/**
 * Props for the ProfileHeaderSummary component.
 */
interface ProfileHeaderSummaryProps {
    profile: BasicProfile;
    tab: 'basic' | 'reports' | 'account' | 'team';
    setTab: (tab: 'basic' | 'reports' | 'account' | 'team') => void;
}

/**
 * The tab options for the profile summary.
 */
// Plans where the owner can manage a team (seats > 1)
const TEAM_PLANS = ['sapphire', 'gold', 'platinum', 'sandbox']

const tabOptions = {
    basic: 'Basic Info',
    reports: 'My Reports',
    account: 'Account Details',
}

/**
 * ProfileHeaderSummary component displays the profile summary details
 * and provides navigation options to view different sections of the profile.
 *
 * @component
 * @param {ProfileHeaderSummaryProps} props - The properties object.
 * @param {BasicProfile} props.profile - The profile data to be displayed.
 * @param {'basic' | 'reports' | 'account'} props.tab - The active tab in the profile summary.
 * @param {(tab: 'basic' | 'reports' | 'account') => void} props.setTab - Callback function to set the active tab.
 *
 * @example
 * <ProfileHeaderSummary
 *   profile={profileData}
 *   tab='basic'
 *   setTab={(tab) => console.log(tab)}
 * />
 */
const ProfileHeaderSummary: React.FC<ProfileHeaderSummaryProps> = ({ profile, tab, setTab }) => {
    const [name, setName] = useState<string>('')
    const { userSubscription } = useAuth()
    const navigate = useNavigate()
    const { addToast } = useToast()

    const planName = (userSubscription?.name ?? '').toLowerCase()
    const showTeamTab = TEAM_PLANS.includes(planName) && !userSubscription?.isOrgMember
    const isPilot = planName === 'pilot'

    const handleGiveFeedback = () => {
        if (PILOT_FEEDBACK_FORM_URL) {
            window.open(PILOT_FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer')
            return
        }
        addToast({ type: 'info', message: 'Feedback form coming soon!' })
    }

    useEffect(() => {
        if (profile) {
            setName(`${profile.firstName} ${profile.lastName}`)
        }
    }, [profile])
    return (
        <div className={styles['profile-header-summary']}>
            <div className={styles['main-wrapper']}>
                <div className={styles['left-section']}>
                    {/* <div className={styles['profile-picture-wrapper']}>
                        <img src={profile.profilePicture.length > 0 ? profile.profilePicture : botImage} alt={`${name}'s profile`} className={styles['profile-picture']} />
                        <div className={styles['camera-overlay']}>
                            <ReusableIcon icon='mdi:camera' className={styles['camera-icon'] || 'camera-icon'} color='#fff' />
                            <p className={styles['camera-text']}>Change photo</p>
                        </div>
                    </div> */}
                    <ProfilePictureWrapper profilePicture={profile.profilePicture} />
                    <div className={styles['profile-details']}>
                        <p className={`${styles['profile-item']} ${styles['name']}`}>{name}</p>
                        <p className={`${styles['profile-item']}`}>
                            <ReusableIcon icon='mdi:location' className={styles['icon'] || 'icon'} color={''}/>
                            {profile.country}
                        </p>
                        <p className={`${styles['profile-item']} ${styles['credit-balance']}`}>{profile.creditBalance} Credits</p>
                    </div>
                </div>
                <div className={styles['right-section']}>
                    <ProfilePlanSummaryCard
                        planName={userSubscription?.name || ''}
                        planDescription={userSubscription?.description || ''}
                        onChangeSubscription={() => navigate('/change-plan')}
                        scheduledCancellation={userSubscription?.scheduledCancellation || false}
                        isPilot={isPilot}
                        onGiveFeedback={handleGiveFeedback}
                    />
                </div>
            </div>
            
            <div className={styles['tab-section']}>
                <div className={styles['tabs']}>
                    {Object.keys(tabOptions).map((key, index) => (
                        <button
                            key={index}
                            className={`${styles['tab-button']} ${tab === key ? styles['active'] : ''}`}
                            onClick={() => setTab(key as 'basic' | 'reports' | 'account' | 'team')}
                        >
                            {tabOptions[key as keyof typeof tabOptions]}
                            {tab === key && <div className={styles['active-indicator']}></div>}
                        </button>
                    ))}
                    {showTeamTab && (
                        <button
                            className={`${styles['tab-button']} ${tab === 'team' ? styles['active'] : ''}`}
                            onClick={() => setTab('team')}
                        >
                            My Team
                            {tab === 'team' && <div className={styles['active-indicator']}></div>}
                        </button>
                    )}
                </div>
                <div className={styles['tab-bar']}></div>
            </div>
        </div>
    )
}

export default ProfileHeaderSummary;